import { useEffect, useRef, useState } from "react";
import type { AuthState } from "./useAuth";
import type { SanctumState } from "../types";
import { consumePendingLegacyState, sanctumSnapshot, useSanctumStore } from "../stores/useSanctumStore";

export type SyncStatus = "local" | "locked" | "loading" | "synced" | "saving" | "offline";

class ArchiveLockedError extends Error {}

async function requireStateResponse(response: Response): Promise<SanctumState> {
  const payload = await response.json().catch(() => ({})) as { state?: SanctumState; code?: string };
  if (!response.ok || !payload.state) {
    if (payload.code === "ARCHIVE_LOCKED") throw new ArchiveLockedError("Archive locked");
    throw new Error("Sync unavailable");
  }
  return payload.state;
}

async function pushState(state: SanctumState): Promise<SanctumState> {
  const response = await fetch("/api/occult/state", {
    method: "PUT", credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ state }),
  });
  return requireStateResponse(response);
}

export function useSanctumSync(auth: AuthState): SyncStatus {
  const updatedAt = useSanctumStore((state) => state.updatedAt);
  const applyRemote = useSanctumStore((state) => state.applyRemote);
  const resetPrivateState = useSanctumStore((state) => state.resetPrivateState);
  const [status, setStatus] = useState<SyncStatus>("local");
  const hydratedUser = useRef<string | null>(null);
  const activeUser = useRef<string | null>(null);
  const ignoreNextSave = useRef(false);

  useEffect(() => {
    if (auth.loading) return;
    const incomingUser = auth.authenticated && auth.user ? auth.user.id : null;
    if (!incomingUser) {
      if (activeUser.current) resetPrivateState();
      activeUser.current = null;
      hydratedUser.current = null;
      setStatus("local");
      return;
    }
    if (activeUser.current && activeUser.current !== incomingUser) {
      resetPrivateState();
      hydratedUser.current = null;
    }
    activeUser.current = incomingUser;
    if (hydratedUser.current === incomingUser) return;
    let cancelled = false;
    setStatus("loading");
    void (async () => {
      try {
        const response = await fetch("/api/occult/state", { credentials: "same-origin", cache: "no-store" });
        const remote = await requireStateResponse(response);
        if (cancelled || activeUser.current !== incomingUser) return;
        const legacy = consumePendingLegacyState();
        if (legacy) applyRemote(legacy);
        const local = legacy || sanctumSnapshot();
        if (remote.updatedAt > local.updatedAt) {
          ignoreNextSave.current = true;
          applyRemote(remote);
        } else if (local.updatedAt > remote.updatedAt) {
          const saved = await pushState(local);
          if (cancelled || activeUser.current !== incomingUser) return;
          ignoreNextSave.current = true;
          applyRemote(saved);
        }
        if (!cancelled) {
          hydratedUser.current = incomingUser;
          setStatus("synced");
        }
      } catch (error) {
        if (!cancelled) setStatus(error instanceof ArchiveLockedError ? "locked" : "offline");
      }
    })();
    return () => { cancelled = true; };
  }, [auth.authenticated, auth.loading, auth.user, applyRemote, resetPrivateState]);

  useEffect(() => {
    if (!auth.authenticated || !auth.user || hydratedUser.current !== auth.user.id || !updatedAt) return;
    if (ignoreNextSave.current) {
      ignoreNextSave.current = false;
      return;
    }
    setStatus("saving");
    const timeout = window.setTimeout(() => {
      void pushState(sanctumSnapshot())
        .then(() => setStatus("synced"))
        .catch((error) => setStatus(error instanceof ArchiveLockedError ? "locked" : "offline"));
    }, 900);
    return () => window.clearTimeout(timeout);
  }, [auth.authenticated, auth.user, updatedAt]);

  return status;
}
